/**
 * 도면 대조 패널 — "내가 만든 맵이 도면 수량과 맞는가"를 층마다 확인한다.
 *
 * 층을 만든 원본 DXF 를 서버가 다시 읽어 기본 객체(주차면·CCTV·비상벨·기둥)를 뽑고, 지금 맵과 종류별
 * 수량을 맞춘다. 위치까지 맞출 수 있으면 도면에만 있는 자리(빨강)와 맵에만 있는 객체(주황)를 맵 위에
 * 표시하고, 목록을 누르면 그 자리로 이동한다.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import type { dia } from '@joint/core'

import { DxfParserService } from '../../../services/DxfParserService'
import { FileStorageService } from '../../../services/FileStorageService'
import { parseCSV } from '../../csv/lib/csvParser'
import { elementCategory } from '../../editor/lib/zoneCctvRelations'
import { getActiveCanvasRenderer } from '../../viewer/canvas/activeRenderer'
import type { Pt } from '../../zone-auto-import/lib/transform'
import {
  compareDrawing,
  csvAnchors,
  fitCsvToMap,
  kindOfCategory,
  type CsvAnchor,
  type DrawingObject,
  type KindResult,
  type MapObject,
} from '../lib/compareDrawing'

const MISSING_COLOR = '#EF4444'
const EXTRA_COLOR = '#F59E0B'
/** 종류마다 목록에 펼쳐 보일 최대 개수 — 수백 개가 어긋나면 레이어 선택 자체가 틀린 것이다. */
const LIST_LIMIT = 30

interface Props {
  graph: dia.Graph | null
  siteId: number
  /** 층을 만든 서버 CSV 이름. 없으면(로컬 파일·손으로 그린 층) 대조할 도면을 모른다. */
  csvFileName: string | null
  /** 층에 남아 있는 CSV 원문 — 있으면 다시 내려받지 않는다. */
  csvRawData: string | null
  onClose: () => void
}

interface Loaded {
  dxfFilename: string
  drawing: DrawingObject[]
  anchors: CsvAnchor[]
}

function collectMapObjects(graph: dia.Graph | null): MapObject[] {
  if (!graph) return []
  return graph.getElements().flatMap((element) => {
    const kind = kindOfCategory(elementCategory(element))
    if (!kind) return []
    // 점 객체는 중심이 도면 점에 오게 놓이고(elementFactory), 주차면 bbox 중심은 칸 중심 — 끌어 옮겨도 따라온다.
    const center = element.getBBox().center()
    const handle = (element.get('data') as { entityHandle?: unknown } | undefined)?.entityHandle
    return [{ id: String(element.id), kind, handle: typeof handle === 'string' ? handle : undefined, x: center.x, y: center.y }]
  })
}

async function loadFloorCsv(siteId: number, csvFileName: string, csvRawData: string | null): Promise<string> {
  if (csvRawData) return csvRawData
  const blob = await FileStorageService.downloadFile(String(siteId), 'csv', csvFileName)
  return blob.text()
}

function focus(pt: Pt): void {
  const renderer = getActiveCanvasRenderer()
  if (!renderer) return
  renderer.centerOn(pt.x, pt.y)
  renderer.setFocusPoint(pt)
}

export const DrawingCheckPanel: React.FC<Props> = ({ graph, siteId, csvFileName, csvRawData, onClose }) => {
  const [loaded, setLoaded] = useState<Loaded | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [mapObjects, setMapObjects] = useState<MapObject[]>(() => collectMapObjects(graph))

  useEffect(() => {
    if (!csvFileName) return
    let cancelled = false
    setError(null)
    Promise.all([
      DxfParserService.fetchDrawingObjects(siteId, csvFileName),
      loadFloorCsv(siteId, csvFileName, csvRawData),
    ])
      .then(([res, csvText]) => {
        if (cancelled) return
        setLoaded({ dxfFilename: res.dxfFilename, drawing: res.objects, anchors: csvAnchors(parseCSV(csvText).entities) })
      })
      .catch((e: unknown) => {
        if (!cancelled) setError(e instanceof Error ? e.message : '도면 대조 실패')
      })
    return () => {
      cancelled = true
    }
  }, [siteId, csvFileName, csvRawData])

  const transform = useMemo(() => (loaded ? fitCsvToMap(loaded.anchors, mapObjects) : null), [loaded, mapObjects])
  const results: KindResult[] = useMemo(
    () => (loaded ? compareDrawing(loaded.drawing, mapObjects, transform) : []),
    [loaded, mapObjects, transform],
  )

  useEffect(() => {
    const renderer = getActiveCanvasRenderer()
    if (!renderer) return
    renderer.setDrawingCheckMarkers(
      results.flatMap((r) => [
        ...r.missing.map((p) => ({ ...p, color: MISSING_COLOR })),
        ...r.extra.map((o) => ({ x: o.x, y: o.y, color: EXTRA_COLOR })),
      ]),
    )
  }, [results])

  useEffect(
    () => () => {
      const renderer = getActiveCanvasRenderer()
      renderer?.setDrawingCheckMarkers([])
      renderer?.setFocusPoint(null)
    },
    [],
  )

  const recompare = useCallback(() => setMapObjects(collectMapObjects(graph)), [graph])
  const allMatch = results.length > 0 && results.every((r) => r.drawing === r.map && !r.missing.length && !r.extra.length)

  return (
    <Paper
      elevation={3}
      sx={{ position: 'absolute', top: 16, right: 16, width: 400, maxHeight: 'calc(100% - 32px)', overflowY: 'auto', p: 2, zIndex: 20 }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography variant="subtitle1" fontWeight={600}>
          도면 대조
        </Typography>
        <IconButton size="small" onClick={onClose} aria-label="닫기">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Stack>
      <Typography variant="caption" color="text.secondary">
        이 층을 만든 도면을 다시 읽어 주차면·CCTV·비상벨·기둥 수량과 위치를 지금 맵과 맞춰 봅니다.
      </Typography>

      {!csvFileName && (
        <Alert severity="info" sx={{ mt: 1.5 }}>
          이 층은 서버 도면(DXF)에서 만든 층이 아니라 맞춰 볼 도면이 없습니다.
        </Alert>
      )}
      {csvFileName && !loaded && !error && (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 2 }}>
          <CircularProgress size={18} />
          <Typography variant="body2">도면 다시 읽는 중… 큰 도면은 1~2분 걸립니다</Typography>
        </Stack>
      )}
      {error && (
        <Alert severity="error" sx={{ mt: 1.5 }}>
          {error}
        </Alert>
      )}

      {loaded && (
        <>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            도면: {loaded.dxfFilename}
          </Typography>
          {!transform && (
            <Alert severity="warning" sx={{ mt: 1 }}>
              맵 객체와 도면 위치를 맞추지 못해 수량만 비교합니다(이 층 CSV 에서 온 객체가 3개 미만).
            </Alert>
          )}
          {allMatch && (
            <Alert severity="success" sx={{ mt: 1 }}>
              도면과 맵이 수량·위치 모두 맞습니다.
            </Alert>
          )}
          <Table size="small" sx={{ mt: 1 }}>
            <TableHead>
              <TableRow>
                <TableCell>종류</TableCell>
                <TableCell align="right">도면</TableCell>
                <TableCell align="right">맵</TableCell>
                <TableCell align="right">차이</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {results.map((r) => {
                const diff = r.map - r.drawing
                return (
                  <TableRow key={r.kind}>
                    <TableCell>{r.label}</TableCell>
                    <TableCell align="right">{r.drawing}</TableCell>
                    <TableCell align="right">{r.map}</TableCell>
                    <TableCell align="right" sx={{ color: diff ? 'error.main' : 'success.main', fontWeight: 600 }}>
                      {diff > 0 ? `+${diff}` : diff}
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>

          {results
            .filter((r) => r.missing.length || r.extra.length)
            .map((r) => (
              <Box key={r.kind} sx={{ mt: 1.5 }}>
                <Typography variant="body2" fontWeight={600}>
                  {r.label}
                </Typography>
                {r.missing.length > 0 && (
                  <MismatchList
                    title={`도면에만 있음 ${r.missing.length}`}
                    color={MISSING_COLOR}
                    points={r.missing}
                  />
                )}
                {r.extra.length > 0 && (
                  <MismatchList title={`맵에만 있음 ${r.extra.length}`} color={EXTRA_COLOR} points={r.extra} />
                )}
              </Box>
            ))}

          <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
            <Button size="small" variant="outlined" onClick={recompare}>
              맵 고친 뒤 다시 대조
            </Button>
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            빨강 고리 = 도면에만 있는 자리, 주황 고리 = 맵에만 있는 객체. 도면은 가져오기 기본 레이어로 다시
            읽은 결과라, 가져올 때 레이어를 빼거나 더했으면 그만큼 차이로 보입니다.
          </Typography>
        </>
      )}
    </Paper>
  )
}

const MismatchList: React.FC<{ title: string; color: string; points: Pt[] }> = ({ title, color, points }) => (
  <Box sx={{ mt: 0.5 }}>
    <Typography variant="caption" sx={{ color, fontWeight: 600 }}>
      {title}
    </Typography>
    <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mt: 0.5 }}>
      {points.slice(0, LIST_LIMIT).map((p, i) => (
        <Button
          key={`${p.x},${p.y}`}
          size="small"
          variant="outlined"
          onClick={() => focus(p)}
          sx={{ minWidth: 44, minHeight: 32, borderColor: color, color }}
        >
          {i + 1}
        </Button>
      ))}
      {points.length > LIST_LIMIT && (
        <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
          외 {points.length - LIST_LIMIT}개 — 맵의 고리로 확인
        </Typography>
      )}
    </Stack>
  </Box>
)
